import React, { useState, useEffect, useRef } from "react";
import { Card, Input, Button, Spin, Typography, Alert, List } from "antd";
import { SendOutlined } from "@ant-design/icons";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "./TeacherAgents.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const TeacherSupportAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      return;
    }

    const connectWebSocket = () => {
      const ws = new WebSocket(
        `ws://localhost:8000/ws/agent/teacher_support?token=${token}`
      );
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected - Teacher Support Agent");
        setConnected(true);
        setError(null);

        // Send initial message
        ws.send(
          JSON.stringify({
            message: "Hello, I need teacher support assistance.",
            context: "teaching_support",
          })
        );
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setMessages((prev) => [
          ...prev,
          {
            text: data.message,
            sender: "agent",
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
        setLoading(false);
      };

      ws.onerror = (e) => {
        console.error("WebSocket error:", e);
        setError("Connection error. Please try again.");
        setConnected(false);
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);

        // Attempt to reconnect after a delay
        setTimeout(() => {
          if (socketRef.current?.readyState === WebSocket.CLOSED) {
            connectWebSocket();
          }
        }, 3000);
      };
    };

    connectWebSocket();

    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  // Handle sending messages
  const handleSend = () => {
    if (
      !input.trim() ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    )
      return;

    const message = {
      text: input,
      sender: "user",
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, message]);
    setLoading(true);
    setInput("");

    socketRef.current.send(
      JSON.stringify({
        message: input,
        context: "teaching_support",
      })
    );
  };

  // Handle pressing Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Layout>
      <div className="teacher-agent-container">
        <Card
          className="teacher-agent-card"
          title={
            <div className="agent-header">
              <Title level={3}>Teacher Support Assistant</Title>
              {connected ? (
                <Text type="success" className="connection-status">
                  Connected
                </Text>
              ) : (
                <Text type="danger" className="connection-status">
                  Disconnected
                </Text>
              )}
            </div>
          }
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className="error-alert"
            />
          )}

          <div className="message-container">
            {messages.length === 0 ? (
              <div className="welcome-message">
                <Title level={4}>Welcome to Teacher Support!</Title>
                <Text>
                  Ask me about teaching strategies, classroom management, lesson
                  planning, or any other teaching-related queries.
                </Text>
              </div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={messages}
                renderItem={(message) => (
                  <div
                    className={`message ${
                      message.sender === "user"
                        ? "user-message"
                        : "agent-message"
                    }`}
                  >
                    <div className="message-content">
                      <div className="message-text">{message.text}</div>
                      <div className="message-timestamp">
                        {message.timestamp}
                      </div>
                    </div>
                  </div>
                )}
              />
            )}
            {loading && (
              <div className="agent-message">
                <div className="message-content">
                  <div className="loading-indicator">
                    <Spin size="small" />{" "}
                    <Text italic>Agent is thinking...</Text>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-area">
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about teaching strategies, classroom management, etc."
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={!connected}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!connected || !input.trim()}
              className="send-button"
            >
              Send
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default TeacherSupportAgent;
